import { Action, Store } from "redux";
import { SagaMiddleware, Task } from "redux-saga";

import { IBridge, NAME } from "@multiweb/reactCore/bridge/api";
import { invariant } from "@multiweb/reactUtils";
import { ILocalizationItem } from "@multiweb/types/contracts";
import { ApplicationModule, IAppState, RequiredProp, SagaWithReturnType } from "@multiweb/types/react";
import { Tail } from "@redux-saga/core/effects";
import { MockBridge } from "../mock";

/**
 * Implementace bridge modulu sloužící jako základ pro stories komplexních komponent využívajících ságy.
 * Poskytuje základní redux funkcionalitu z rozhraní {@link IBridgeCore}. Dále poskytuje funkcionalitu
 * lokalizací a možnost lehce nadefinovat lokalizace pomocí metody {@link setLocalizations}.
 *
 * Při overridování metod je nutné dodržet "property initializer syntax", jinak se override neprovede.
 *
 * Pro správnou funkci je nutné vytvořit a nastavit obslužné ságy:
 * @example Použití
 * import { StoryBridge, createStoryBridgeSaga } from "@multiweb/reactCore/devBridge";
 *
 * const bridge = new StoryBridge();
 * const saga = createStoryBridgeSaga(bridge);
 * bridge.setSaga(saga);
 *
 * export const testStory = createComponentStoryParams({
 *     ...
 *     decorators: [
 *         ...
 *         withSagaStore(bridge),
 *     ],
 * });
 */
export class StoryBridge extends MockBridge implements IBridge {
    /**
     * Proměnná uchovávající store pro vysílání akcí. K této proměnné by se nikdy nemělo přistupovat přímo,
     * k tomu slouží metoda getStore.
     * @private
     */
    private _store: Store<IAppState> | null = null;

    /**
     * Proměnná uchovávající funkci pro spuštění ságy. K této proměnné by se nikdy nemělo přistupovat přímo,
     * k tomu slouží metoda getRunSaga.
     * @private
     */
    private _runSaga: SagaMiddleware["run"] | null = null;

    /**
     * Kořenová sága bridge modulu.
     * @private
     */
    private _saga: SagaWithReturnType | null = null;

    /**
     * Získá store.
     * @private
     * @throws V případě že store není nastavená vyhazuje chybu.
     */
    private readonly _getStore = () => {
        invariant(this._store, "[Bridge module] StoryBridge module store must be initialized by setStore method before it can be used.");
        return this._store;
    };

    /**
     * Nastaví ságu bridge modulu. Nemůže být importována staticky kvůli cyklické závislosti. Proto se nastaví až po vytvoření instance.
     * @param saga Kořenová sága bridge modulu.
     */
    public readonly setSaga = (saga: SagaWithReturnType): void => {
        this._saga = saga;
    };

    public override get module(): ApplicationModule<typeof NAME> {
        invariant(this._saga !== null, "StoryBridge module was not properly initialized by setting bridge saga.");
        return {
            NAME,
            saga: this._saga,
        };
    }

    public override readonly setStore = (storeParam: Store<IAppState>, runSaga: SagaMiddleware["run"]): void => {
        this._store = storeParam;
        this._runSaga = runSaga;
    };

    public override readonly errorHandler = (error: unknown): void => {
        alert(`[StoryBridge] Error handler has been triggered with following error: ${error instanceof Error ? error.message : error}`);
        // eslint-disable-next-line no-console
        console.error(error);// Pro lepší debug ve storybooku. Ve storybooku není potřeba používat logger.
    };

    public override readonly dispatchAction = (action: Action): void => {
        this._getStore().dispatch(action);
    };

    public override readonly runSaga = <S extends SagaWithReturnType<any[]>>(saga: S, ...args: Parameters<S>): Task => {
        invariant(this._runSaga, "[Bridge module] StoryBridge module store must be initialized by setStore method before it can be used.");
        return this._runSaga(saga, ...args);
    };

    public override readonly getState = (): IAppState => {
        return this._getStore().getState();
    };

    public override readonly useSelector = <Fn extends(state: IAppState, ...args: Tail<Parameters<Fn>>) => ReturnType<Fn>>(selector: Fn, ...args: Tail<Parameters<Fn>>): ReturnType<Fn> => {
        return selector(this.getState(), ...args);
    };

    private readonly _localizationMap: Map<string, ILocalizationItem> = new Map();

    /**
     * Nastaví lokalizace, které jsou pak vracené jako výsledek při volání {@link loadLocalizationItems}.
     * K lokalizačním položkám automaticky přidá výchozí hodnoty, pokud nejsou hodnoty přítomné:
     * language: "cs",
     * primaryText: - nastaví se lokalizační klíč
     *
     * @param locItems Lokalizační položky k nastavení.
     */
    public readonly setLocalizations = (...locItems: RequiredProp<Partial<ILocalizationItem>, "locKey">[]) => {
        locItems.forEach((locItem) => {
            const { locKey, primaryText, secondaryText, language } = locItem;
            this._localizationMap.set(locKey, {
                locKey,
                language: language ?? "cs",
                primaryText: primaryText ?? locKey,
                secondaryText,
            });
        });
    };

    public override readonly loadLocalizationItems = (_locKeys: readonly string[]): Promise<Map<string, ILocalizationItem>> => {
        return Promise.resolve(this._localizationMap);
    };
}
